import { createPortal } from 'react-dom'

import { LoadingBar } from '../../components/LoadingBar'
import { useEscape } from '../../hooks/useEscape'
import { useTexte } from '../../texte'

export type FilmStep = 'prepare' | 'map' | 'compute' | 'upload'

export interface FilmProgress {
  step: FilmStep
  share: number
  /**
   * What the film will be without (1.141.0), said while it is made. From
   * use: "die Meldung (ohne Höhenprofil) bleibt stehen und geht nicht mehr
   * weg (sollte nur eine Info sein, während das Video generiert wird)".
   */
  note?: string
}

const STEPS: FilmStep[] = ['prepare', 'map', 'compute', 'upload']

/**
 * The modal sheet while the film of a day card is made (#90, 1.96.0).
 *
 * Decided: "Veröffentlichen / Senden der Streckenstatistik erst nachdem das
 * modale Fenster fertig ist. bei Abbruch des Benutzers / Verlassen der App:
 * nichts am Server, muss dann neu gestartet werden." So the sheet stands
 * until the card is sent, nothing behind it can be tapped, and the one way
 * out is "Abbrechen" (or Escape) -- which sends nothing.
 *
 * Steps, because the bar runs from 0 to 100 three times and would
 * otherwise look like it started over: the tiles, the frames, the upload.
 *
 * **And a first step before them, from the tap on** (1.125.0). From use,
 * with a recording: "die Zeitspanne zwischen ‚Speichern' und der Anzeige des
 * Fensters mit dem Status der Erstellung ist zu lang ... das Fenster sollte
 * sofort angezeigt werden, damit der Benutzer weiß, dass etwas passiert."
 * The sheet opened only after the server had read the day (with heights for
 * the complex film), the encoders were asked and the script was built --
 * measured on the recording, more than a second with nothing on screen.
 * That wait has no share to tell, so it runs the `LoadingBar` and no percent.
 */
export function RouteFilmSheet({ progress, onCancel }: { progress: FilmProgress; onCancel: () => void }) {
  const t = useTexte()
  const f = t.chat.film
  useEscape(onCancel)
  const percent = Math.round(Math.max(0, Math.min(1, progress.share)) * 100)
  const current = STEPS.indexOf(progress.step)
  return createPortal(
    <div className="rueckfrage" role="dialog" aria-modal="true" aria-label={f.title} data-route-film={progress.step}>
      <div className="rueckfrage__blatt" onClick={(event) => event.stopPropagation()}>
        <p className="rueckfrage__frage">{f.title}</p>
        <ol className="route-film__steps">
          {STEPS.map((s, i) => (
            <li
              key={s}
              className={i < current ? 'route-film__step route-film__step--done' : i === current ? 'route-film__step route-film__step--current' : 'route-film__step'}
              aria-current={i === current ? 'step' : undefined}
            >
              {f[s]}
            </li>
          ))}
        </ol>
        {progress.step === 'prepare' ? (
          <LoadingBar label={f.prepare} />
        ) : (
          <>
            <div className="route-film__bar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={percent} aria-label={f[progress.step]}>
              <span style={{ width: `${percent}%` }} />
            </div>
            <p className="leise route-film__share" data-route-film-share={percent}>{f.share(percent)}</p>
          </>
        )}
        {progress.note !== undefined && <p className="leise" data-route-film-note="">{progress.note}</p>}
        <p className="leise">{f.hint}</p>
        <div className="rueckfrage__knoepfe">
          <button type="button" className="button button--outline button--small" onClick={onCancel} autoFocus data-route-film-cancel="">
            {t.allgemein.abbrechen}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
